import React, { useState, useEffect } from "react";
import {
  Card,
  Col,
  Row,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function EmployeeAutoPayroll() {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userPayroll, setUserPayroll] = useState(null);
  const [clockEntries, setClockEntries] = useState([]);
  const [calculatedPayroll, setCalculatedPayroll] = useState(null);
  
  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          setCurrentUser(user);
          await fetchUserPayrollData(user.email);
        }
      });
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (userPayroll && clockEntries.length > 0) {
      calculateEmployeePayroll();
    }
  }, [userPayroll, clockEntries, selectedMonth]);

  const fetchUserPayrollData = async (email) => {
    setLoading(true);
    try {
      // Fetch user's payroll settings
      const payrollQuery = query(collection(db, "payrolls"), where("email", "==", email));
      const payrollSnapshot = await getDocs(payrollQuery);
      
      if (!payrollSnapshot.empty) {
        const payrollData = payrollSnapshot.docs[0].data();
        setUserPayroll(payrollData);
      } else {
        setError("No payroll settings found for your account. Please contact HR.");
        return;
      }

      // Fetch user's clock entries
      const clockQuery = query(collection(db, "clock_entries"), where("email", "==", email));
      const clockSnapshot = await getDocs(clockQuery);
      const clockData = clockSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClockEntries(clockData);

    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Failed to load payroll data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const calculateEmployeePayroll = () => {
    if (!userPayroll) return;

    const startOfMonth = new Date(`${selectedMonth}-01T00:00:00`);
    const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0, 23, 59, 59);
    
    // Get user's clock entries for the selected month
    const monthlyClockEntries = clockEntries.filter(entry => {
      const clockIn = new Date(entry.clockIn);
      return clockIn >= startOfMonth && 
             clockIn <= endOfMonth &&
             entry.clockOut; // Only completed entries
    });

    // Calculate total hours worked
    let totalHours = 0;
    let totalBreakMinutes = 0;
    const dailyEntries = [];

    monthlyClockEntries.forEach(entry => {
      const clockIn = new Date(entry.clockIn);
      const clockOut = new Date(entry.clockOut);
      const duration = (clockOut - clockIn) / (1000 * 60 * 60); // Convert to hours
      
      // Calculate break time
      const breaks = entry.breaks || [];
      const breakMinutes = breaks.reduce((sum, breakItem) => sum + (breakItem.duration || 0), 0);
      totalBreakMinutes += breakMinutes;
      
      const netHours = duration - (breakMinutes / 60);
      totalHours += netHours;

      dailyEntries.push({
        date: clockIn.toISOString().split('T')[0],
        clockIn: clockIn.toLocaleTimeString('en-GB'),
        clockOut: clockOut.toLocaleTimeString('en-GB'),
        totalHours: duration.toFixed(2),
        breakMinutes: breakMinutes,
        netHours: netHours.toFixed(2)
      });
    });

    // Calculate payroll
    const hourlyRate = userPayroll.biweeklySalary / userPayroll.biweeklyHours;
    const grossPay = totalHours * hourlyRate;
    const breakHours = totalBreakMinutes / 60;

    setCalculatedPayroll({
      totalHours: totalHours.toFixed(2),
      breakHours: breakHours.toFixed(2),
      hourlyRate: hourlyRate.toFixed(2),
      grossPay: grossPay.toFixed(2),
      dailyEntries,
      payrollSettings: userPayroll,
      month: selectedMonth,
      workingDays: dailyEntries.length
    });
  };

  const exportToCSV = () => {
    if (!calculatedPayroll) return;

    const csvContent = [
      ['Date', 'Clock In', 'Clock Out', 'Total Hours', 'Break Time (min)', 'Net Hours', 'Hourly Rate', 'Daily Pay'],
      ...calculatedPayroll.dailyEntries.map(entry => [
        new Date(entry.date).toLocaleDateString('en-GB'),
        entry.clockIn,
        entry.clockOut,
        entry.totalHours,
        entry.breakMinutes,
        entry.netHours,
        calculatedPayroll.hourlyRate,
        (parseFloat(entry.netHours) * parseFloat(calculatedPayroll.hourlyRate)).toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-payroll-${selectedMonth}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="text-center">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3 text-muted">Loading your payroll data...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Alert variant="danger" className="text-center">
            <i className="ri-error-warning-line fs-1 mb-3"></i>
            <h4>Error Loading Payroll Data</h4>
            <p>{error}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              <i className="ri-refresh-line me-2"></i>
              Try Again
            </Button>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Header Section */}
        <div className="d-flex align-items-center mb-4">
          <div className="me-3">
            <i className="ri-calculator-line display-6 text-primary"></i>
          </div>
          <div>
            <h1 className="display-6 fw-bold text-primary mb-1">My Payroll</h1>
            <p className="lead text-muted mb-0">View your calculated payroll based on clock entries</p>
          </div>
        </div>

        {/* Month Selector */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <Card.Header className="bg-gradient-primary text-white p-4 rounded-top-4">
            <div className="d-flex align-items-center">
              <i className="ri-calendar-line me-3 fs-4"></i>
              <h4 className="fw-bold mb-0">Select Payroll Period</h4>
            </div>
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <i className="ri-calendar-line me-2"></i>
                    Select Month
                  </Form.Label>
                  <Form.Control
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                <Button
                  variant="outline-primary"
                  onClick={exportToCSV}
                  disabled={!calculatedPayroll || calculatedPayroll.dailyEntries.length === 0}
                  className="px-4 py-3 rounded-pill fw-semibold"
                >
                  <i className="ri-download-line me-2"></i>
                  Export My Payroll
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {calculatedPayroll && calculatedPayroll.dailyEntries.length > 0 ? (
          <>
            {/* Summary Cards */}
            <Row className="g-4 mb-5">
              <Col md={3}>
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
                  <Card.Header 
                    className="bg-gradient-primary text-white p-4"
                    style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="ri-time-line fs-4 me-3"></i>
                      <h6 className="fw-bold mb-0">Total Hours</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4 text-center">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                      <i className="ri-time-line fs-1 text-primary"></i>
                    </div>
                    <h3 className="fw-bold text-primary">{calculatedPayroll.totalHours}h</h3>
                    <p className="text-muted small mb-0">Hours worked</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
                  <Card.Header 
                    className="bg-gradient-warning text-white p-4"
                    style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="ri-pause-circle-line fs-4 me-3"></i>
                      <h6 className="fw-bold mb-0">Break Hours</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4 text-center">
                    <div className="bg-warning bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                      <i className="ri-pause-circle-line fs-1 text-warning"></i>
                    </div>
                    <h3 className="fw-bold text-warning">{calculatedPayroll.breakHours}h</h3>
                    <p className="text-muted small mb-0">Break time</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
                  <Card.Header 
                    className="bg-gradient-success text-white p-4"
                    style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="ri-money-dollar-circle-line fs-4 me-3"></i>
                      <h6 className="fw-bold mb-0">Hourly Rate</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4 text-center">
                    <div className="bg-success bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                      <i className="ri-money-dollar-circle-line fs-1 text-success"></i>
                    </div>
                    <h3 className="fw-bold text-success">${calculatedPayroll.hourlyRate}</h3>
                    <p className="text-muted small mb-0">Per hour</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
                  <Card.Header 
                    className="bg-gradient-info text-white p-4"
                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="ri-calculator-line fs-4 me-3"></i>
                      <h6 className="fw-bold mb-0">Gross Pay</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4 text-center">
                    <div className="bg-info bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3">
                      <i className="ri-calculator-line fs-1 text-info"></i>
                    </div>
                    <h3 className="fw-bold text-info">${calculatedPayroll.grossPay}</h3>
                    <p className="text-muted small mb-0">This month</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Payroll Details Table */}
            <Card className="border-0 shadow-lg rounded-4">
              <Card.Header className="bg-gradient-success text-white p-4 rounded-top-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="ri-file-list-3-line fs-4 me-3"></i>
                    <h4 className="fw-bold mb-0">Daily Clock Entries</h4>
                    <Badge bg="light" text="dark" className="ms-3 px-3 py-2">
                      {calculatedPayroll.workingDays} days
                    </Badge>
                  </div>
                  <div className="text-end">
                    <small className="opacity-75">
                      {new Date(selectedMonth + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                    </small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table className="table-hover table-borderless mb-0">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="border-0 p-3">
                          <i className="ri-calendar-line me-2"></i>
                          Date
                        </th>
                        <th className="border-0 p-3">
                          <i className="ri-login-circle-line me-2"></i>
                          Clock In
                        </th>
                        <th className="border-0 p-3">
                          <i className="ri-logout-circle-line me-2"></i>
                          Clock Out
                        </th>
                        <th className="border-0 p-3">
                          <i className="ri-time-line me-2"></i>
                          Total Hours
                        </th>
                        <th className="border-0 p-3">
                          <i className="ri-pause-circle-line me-2"></i>
                          Break Time
                        </th>
                        <th className="border-0 p-3">
                          <i className="ri-calculator-line me-2"></i>
                          Net Hours
                        </th>
                        <th className="border-0 p-3">
                          <i className="ri-money-dollar-circle-line me-2"></i>
                          Daily Pay
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculatedPayroll.dailyEntries.map((entry, index) => (
                        <tr 
                          key={index}
                          style={{ 
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.target.closest('tr').style.backgroundColor = '#f8f9fa';
                            e.target.closest('tr').style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.closest('tr').style.backgroundColor = 'transparent';
                            e.target.closest('tr').style.transform = 'translateY(0)';
                          }}
                        >
                          <td className="p-3">
                            <span className="fw-semibold">
                              {new Date(entry.date).toLocaleDateString('en-GB')}
                            </span>
                          </td>
                          <td className="p-3">
                            <Badge bg="success" className="rounded-pill px-3 py-2">
                              {entry.clockIn}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge bg="danger" className="rounded-pill px-3 py-2">
                              {entry.clockOut}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <span className="fw-semibold">{entry.totalHours}h</span>
                          </td>
                          <td className="p-3">
                            <Badge bg="warning" text="dark" className="rounded-pill px-3 py-2">
                              {entry.breakMinutes}m
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge bg="info" className="rounded-pill px-3 py-2">
                              {entry.netHours}h
                            </Badge>
                          </td>
                          <td className="p-3">
                            <span className="fw-bold text-success fs-6">
                              ${(parseFloat(entry.netHours) * parseFloat(calculatedPayroll.hourlyRate)).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : (
          <Card className="border-0 shadow-lg rounded-4">
            <Card.Body className="text-center py-5">
              <i className="ri-file-list-3-line display-1 text-muted mb-3"></i>
              <h5 className="text-muted">No Payroll Data Found</h5>
              <p className="text-muted">
                No clock entries found for {new Date(selectedMonth + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}. 
                Make sure you have clocked in and out during this period.
              </p>
              <Button 
                variant="primary" 
                onClick={() => window.location.href = '/apps/clockinclockout'}
                className="px-4 py-2 rounded-pill"
              >
                <i className="ri-time-line me-2"></i>
                Go to Clock In/Out
              </Button>
            </Card.Body>
          </Card>
        )}
      </div>
      <Footer />
    </>
  );
}
